import _ from 'lodash'
import * as React from 'react'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import { useNavigate, useLocation } from 'react-router-dom'
import * as RT from '../../config/routes'
import { useDispatch, useSelector } from 'react-redux'
import { AlertContext } from '../../context/AlertContext'
import { setAddMode, setTreeCoords, setPopup } from '../../redux/slices/Map/map'
import { show as openDrawerRight } from '../../redux/slices/Drawer/drawerRight'
import { Home, Forest, AddLocationAlt, Summarize } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import TooltipMain from './TooltipMain'
import { detectCityFromURL, getCityInfo } from '../../utils/cityDetector'
import { saveViewport } from '../../utils/mapViewport'
import { useCities } from '../../context/CitiesContext'
import { buildCantonUrl } from '../../utils/cantonUrl'

const WINDOWWIDTHSIZE = 840
const FONTSIZE_TEXT = '0.9em'

const IconBreadcrumbs = ({ windowWidth, map }) => {
  const [isHovered, setIsHovered] = React.useState(false)
  const dispatch = useDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { alertRef } = React.useContext(AlertContext)
  const { getCityBySlug, getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()

  const popupState = useSelector(state => state.mapState.popup)
  const treeCode = popupState.code
  const popupOpen = popupState.open
  const currentCity = detectCityFromURL(location.pathname)

  const isEditRoute = location.pathname.includes(RT.MANAGEMENT)
  const isRegisterRoute = location.pathname.includes(RT.REGISTER_TREE)
  const isReportRoute = _.eq(location.pathname, RT.REPORT_ROUTE)
  const isMobile = windowWidth < 800
  const isNarrow = windowWidth < WINDOWWIDTHSIZE

  /* ------------------------------
     UTILIDADES
  --------------------------------*/

  const alert = (msg, type = 'success') => {
    const r = alertRef?.current
    if (!r) return
    r.message(msg)
    r.isSeverity(type)
    r.openAlert(true)
  }

  const closePopup = () => {
    dispatch(setPopup({ open: false, code: null, id: null }))
  }

  const resetUIState = () => {
    dispatch(openDrawerRight(false))
    dispatch(setAddMode(false))
    dispatch(setTreeCoords(null))
  }

  const saveCityViewport = res => {
    saveViewport({ city: res.slug, center: res.center, zoom: res.zoom })
  }

  /* ------------------------------
     HANDLER PRINCIPAL
  --------------------------------*/

  const handleOnClickMain = async () => {
    resetUIState()

    if (!currentCity || location.pathname === `/${currentCity}`) {
      navigate(RT.MAIN, { replace: true })
      return
    }

    try {
      const cityConfig = getCityBySlug(currentCity)
      const res = await getCityInfo(cityConfig)
      if (!res) return

      saveCityViewport(res)

      // Caso: Editar o Registrar con popup abierto → ir a árbol concreto
      if ((isEditRoute || isRegisterRoute) && treeCode && popupOpen) {
        // El popup ya está posicionado en el árbol, solo navegamos
        navigate(buildCantonUrl(currentCity, cantones, `/${treeCode}`), { replace: true })
        return alert(`Árbol ${treeCode}`)
      }

      // Caso: Editar o Registrar sin popup → volver a ciudad
      if (isEditRoute || isRegisterRoute) {
        navigate(buildCantonUrl(currentCity, cantones), { replace: true })
        map.flyTo({
          center: res.center,
          zoom: res.zoom,
          speed: 1.2,
          curve: 1,
          essential: true,
        })
        return alert(`Mapa de ${res.name}`)
      }

      // Ruta normal → cerrar popup y volver a ciudad
      closePopup()
      navigate(buildCantonUrl(currentCity, cantones), { replace: true })
      map.easeTo({
        center: res.center,
        zoom: res.zoom,
        duration: 800,
        easing: t => 1 - Math.pow(1 - t, 3),
      })

      alert(`Mapa de ${res.name}`)
    } catch (err) {
      console.error('Error handleOnClickMain:', err)
    }
  }

  /* ------------------------------
     RENDER Tooltip Inicio
  --------------------------------*/

  const iconStyles = {
    variant: 'contained',
    sx: {
      color: theme.palette.text.white,
      stroke: 'black',
      fontSize: 'inherit',
    },
  }

  const sharedTooltipProps = {
    location,
    fontsizetext: FONTSIZE_TEXT,
  }

  const mainTooltip = (
    <TooltipMain
      id="inicio"
      text="Inicio"
      icon={<Home {...iconStyles} />}
      onClick={handleOnClickMain}
      ishovered={isHovered}
      to={currentCity ? `/${currentCity}` : RT.MAIN}
      {...sharedTooltipProps}
    />
  )

  /* ------------------------------
     RENDER MOBILE
  --------------------------------*/

  if (isMobile)
    return (
      <div role="presentation" style={{ height: 'inherit' }}>
        <Breadcrumbs
          aria-label="breadcrumb-home"
          sx={{
            display: 'flex',
            height: 'inherit',
            borderBottom: '2px solid transparent',
            '&:hover': { borderBottom: `2px solid ${theme.palette.secondary.light}` },
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}>
          {mainTooltip}
        </Breadcrumbs>
      </div>
    )

  /* ------------------------------
     RENDER DESKTOP
  --------------------------------*/

  return (
    <div role="presentation" style={{ height: 'inherit' }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ display: 'flex', height: 'inherit' }}>
        {mainTooltip}

        {isEditRoute && (
          <TooltipMain
            id="editar-arboles"
            text={isNarrow ? 'Editar' : 'Editar árbol'}
            icon={<Forest {...iconStyles} />}
            to={location.pathname}
            {...sharedTooltipProps}
          />
        )}

        {isRegisterRoute && (
          <TooltipMain
            id="crear-arbol"
            text={isNarrow ? 'Crear' : 'Crear árbol'}
            icon={<AddLocationAlt {...iconStyles} />}
            to={location.pathname}
            {...sharedTooltipProps}
          />
        )}

        {isReportRoute && (
          <TooltipMain
            id="generar-reporte"
            text="Generar Reporte"
            icon={<Summarize {...iconStyles} />}
            to={location.pathname}
            {...sharedTooltipProps}
          />
        )}
      </Breadcrumbs>
    </div>
  )
}

export default IconBreadcrumbs
